const API_BASE = process.env.REACT_APP_API_BASE || "/api";

const DAY_FR_TO_ENUM = {
  Lundi: "LUNDI",
  Mardi: "MARDI",
  Mercredi: "MERCREDI",
  Jeudi: "JEUDI",
  Vendredi: "VENDREDI",
  Samedi: "SAMEDI",
  Dimanche: "DIMANCHE",
};
const DAY_ENUM_TO_FR = Object.fromEntries(
  Object.entries(DAY_FR_TO_ENUM).map(([fr, en]) => [en, fr])
);

const STATUS_TO_ENUM = {
  work: "WORK",
  off: "OFF",
  holiday: "HOLIDAY",
  vacation: "VACATION",
};
const STATUS_FROM_ENUM = Object.fromEntries(
  Object.entries(STATUS_TO_ENUM).map(([k, v]) => [v, k])
);

const SUBMISSION_TO_FRONT = {
  PENDING: undefined,
  SUBMITTED: "employee_submitted",
  VALIDATED: "manager_validated",
  REJECTED: "rejected",
};

function combineShift(morning, afternoon) {
  const m = (morning || "").trim();
  const a = (afternoon || "").trim();
  const parts = [m, a].filter(Boolean);
  if (parts.length === 0) return "-";
  return parts.join(" / ");
}

function splitShift(shiftString) {
  if (!shiftString || shiftString === "-") return { morning: "", afternoon: "" };
  const parts = shiftString.split("/").map((p) => p.trim());
  if (parts.length === 1) return { morning: parts[0], afternoon: "" };
  return { morning: parts[0], afternoon: parts[1] };
}

function currentMondayISO() {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function currentMonthISO() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function addDays(isoDate, deltaDays) {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function addMonths(monthIso, deltaMonths) {
  const [y, m] = monthIso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0));
  d.setUTCMonth(d.getUTCMonth() + deltaMonths);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(options.body && !(options.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...(options.headers || {}),
    },
    ...options,
    body:
      options.body && !(options.body instanceof FormData)
        ? JSON.stringify(options.body)
        : options.body,
  });

  if (res.status === 204) return null;

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = new Error((data && data.error) || `http_${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const api = {
  base: API_BASE,
  currentMondayISO,
  currentMonthISO,
  addDays,
  addMonths,
  combineShift,
  splitShift,

  async me() {
    const data = await request("/auth/me");
    return data.user;
  },

  async loginEmployee(name, pin) {
    const data = await request("/auth/login", {
      method: "POST",
      body: { mode: "employee", name, pin },
    });
    return data.user;
  },

  async loginManager(code) {
    const data = await request("/auth/login", {
      method: "POST",
      body: { mode: "manager", code },
    });
    return data.user;
  },

  async logout() {
    await request("/auth/logout", { method: "POST" });
  },

  async changePin(currentPin, newPin) {
    return request("/auth/change-pin", {
      method: "POST",
      body: { currentPin, newPin },
    });
  },

  async listEmployees() {
    const data = await request("/employees");
    return data.employees;
  },

  async listEmployeeNamesPublic() {
    const data = await request("/employees/public-names");
    return data.names;
  },

  async createEmployee(name, pin, hourlyRate, contractBase, contractHoursPerWeek) {
    const data = await request("/employees", {
      method: "POST",
      body: {
        name,
        pin,
        hourlyRate,
        contractBase,
        contractHoursPerWeek,
      },
    });
    return data.employee;
  },

  async deactivateEmployee(id) {
    return request(`/employees/${id}`, { method: "DELETE" });
  },

  async resetEmployeePin(id, newPin) {
    return request(`/employees/${id}/reset-pin`, {
      method: "POST",
      body: { newPin },
    });
  },

  async getPendingSubmissions() {
    const data = await request("/planning/pending-submissions");
    return data;
  },

  async getPlanningHiddenWeeks() {
    const data = await request("/planning/hidden-weeks");
    return Array.isArray(data?.hiddenWeeks) ? data.hiddenWeeks : [];
  },

  async setPlanningWeekPublished(week, published) {
    return request("/planning/week-published", {
      method: "PUT",
      body: { week, published },
    });
  },

  async getPlanning(week) {
    const data = await request(`/planning?week=${encodeURIComponent(week)}`);
    const employees = data.employees.map((e) => e.name);
    const employeeIdByName = Object.fromEntries(
      data.employees.map((e) => [e.name, e.id])
    );
    const employeeMeta = Object.fromEntries(
      data.employees.map((e) => [e.name, e])
    );

    const shifts = [];
    const employeeDayStatus = {};
    for (const s of data.shifts) {
      const emp = data.employees.find((e) => e.id === s.userId);
      if (!emp) continue;
      const day = DAY_ENUM_TO_FR[s.day];
      const status = STATUS_FROM_ENUM[s.status] || "work";
      const shiftStr =
        s.status === "WORK" ? combineShift(s.morning, s.afternoon) : "-";
      shifts.push({ employee: emp.name, day, shift: shiftStr });
      employeeDayStatus[emp.name] = employeeDayStatus[emp.name] || {};
      employeeDayStatus[emp.name][day] = status;
    }

    const actualShifts = {};
    for (const a of data.actuals) {
      const emp = data.employees.find((e) => e.id === a.userId);
      if (!emp) continue;
      const day = DAY_ENUM_TO_FR[a.day];
      actualShifts[emp.name] = actualShifts[emp.name] || {};
      actualShifts[emp.name][day] = combineShift(a.morning, a.afternoon);
    }

    const weeklySubmissions = {};
    for (const s of data.submissions) {
      const emp = data.employees.find((e) => e.id === s.userId);
      if (!emp) continue;
      const front = SUBMISSION_TO_FRONT[s.status];
      if (front) weeklySubmissions[emp.name] = front;
    }

    return {
      weekStart: data.weekStart,
      employees,
      employeeIdByName,
      employeeMeta,
      shifts,
      employeeDayStatus,
      actualShifts,
      weeklySubmissions,
    };
  },

  async setShift({ userId, week, dayFr, status, morning, afternoon }) {
    return request("/planning/shift", {
      method: "PUT",
      body: {
        userId,
        week,
        day: DAY_FR_TO_ENUM[dayFr],
        status: STATUS_TO_ENUM[status] || "WORK",
        morning: morning || "",
        afternoon: afternoon || "",
      },
    });
  },

  async setActual({ userId, week, dayFr, morning, afternoon }) {
    return request("/actuals", {
      method: "PUT",
      body: {
        userId,
        week,
        day: DAY_FR_TO_ENUM[dayFr],
        morning: morning || "",
        afternoon: afternoon || "",
      },
    });
  },

  async submitWeek(week) {
    return request("/planning/submit", {
      method: "POST",
      body: { week },
    });
  },

  async validateWeek({ userId, week, status }) {
    const enumStatus =
      status === "validated"
        ? "VALIDATED"
        : status === "rejected"
        ? "REJECTED"
        : "PENDING";
    return request("/planning/validate", {
      method: "POST",
      body: { userId, week, status: enumStatus },
    });
  },

  async getPayroll(week) {
    const qs = week ? `?week=${encodeURIComponent(week)}` : "";
    const data = await request(`/payroll${qs}`);
    return data;
  },

  async getMonthlyHours(month) {
    const data = await request(`/payroll/monthly?month=${encodeURIComponent(month)}`);
    return data;
  },

  async getValidatedKpi(period, month, week) {
    const params = new URLSearchParams();
    params.set("period", period);
    if (month) params.set("month", month);
    if (week) params.set("week", week);
    const data = await request(`/payroll/validated-kpi?${params.toString()}`);
    return data;
  },

  async getWorkedKpi(period, month, week) {
    const params = new URLSearchParams();
    params.set("period", period);
    if (month) params.set("month", month);
    if (week) params.set("week", week);
    const data = await request(`/payroll/worked-kpi?${params.toString()}`);
    return data;
  },

  async getStatusKpi(period, month, week) {
    const params = new URLSearchParams();
    params.set("period", period);
    if (month) params.set("month", month);
    if (week) params.set("week", week);
    const data = await request(`/payroll/status-kpi?${params.toString()}`);
    return data;
  },

  async updateEmployeeRate(id, hourlyRate, effectiveFrom) {
    return request(`/payroll/${id}/rate`, {
      method: "POST",
      body: { hourlyRate, effectiveFrom },
    });
  },
};

export default api;
